import { baseApi } from '@/store/api/baseApi';
import type {
  AdjustInventoryPayload,
  InventoryItem,
  InventoryListParams,
  InventorySummary,
  InventorySummaryParams,
  PaginatedInventory,
  Warehouse,
} from '@/types/inventory';

function buildInventoryFilterQuery(
  params: InventorySummaryParams,
): string {
  const searchParams = new URLSearchParams();

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.warehouseId) searchParams.set('warehouseId', params.warehouseId);
  if (params.stockStatus && params.stockStatus !== 'all') {
    searchParams.set('stockStatus', params.stockStatus);
  }
  if (params.lowStockThreshold != null) {
    searchParams.set('lowStockThreshold', String(params.lowStockThreshold));
  }

  return searchParams.toString();
}

function buildInventoryListQuery(params: InventoryListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'sku');
  searchParams.set('sortOrder', params.sortOrder ?? 'asc');

  const filterQuery = buildInventoryFilterQuery(params);
  if (filterQuery) {
    new URLSearchParams(filterQuery).forEach((value, key) => {
      searchParams.set(key, value);
    });
  }

  return searchParams.toString();
}

export const inventoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getInventory: builder.query<PaginatedInventory, InventoryListParams>({
      query: (params) => ({
        url: `/inventory?${buildInventoryListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildInventoryListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildInventoryListQuery(currentArg ?? {}) !== buildInventoryListQuery(previousArg ?? {}),
      providesTags: [{ type: 'Inventory', id: 'LIST' }],
    }),
    getWarehouses: builder.query<Warehouse[], void>({
      query: () => '/inventory/warehouses',
      transformResponse: (response: { results: Warehouse[] }) => response.results,
      providesTags: [{ type: 'Inventory', id: 'WAREHOUSES' }],
    }),
    getInventorySummary: builder.query<InventorySummary, InventorySummaryParams>({
      query: (params) => ({
        url: `/inventory/summary?${buildInventoryFilterQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildInventoryFilterQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildInventoryFilterQuery(currentArg ?? {}) !==
        buildInventoryFilterQuery(previousArg ?? {}),
      providesTags: [{ type: 'Inventory', id: 'SUMMARY' }],
    }),
    adjustInventory: builder.mutation<InventoryItem, AdjustInventoryPayload>({
      query: (body) => ({ url: '/inventory/adjust', method: 'POST', body }),
      invalidatesTags: [
        { type: 'Inventory', id: 'LIST' },
        { type: 'Inventory', id: 'SUMMARY' },
        { type: 'Products', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetInventoryQuery,
  useGetWarehousesQuery,
  useGetInventorySummaryQuery,
  useAdjustInventoryMutation,
} = inventoryApi;
